import {
  StructuredLogger,
  logCorrelationId,
  logFailureReason,
} from '@brighthub/structured-logging';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import {
  ApiService,
  GenericResponseCode,
  GenericResponseResult,
} from '@brighthub/api';
import { SentryService } from '@brighthub/logging';
import { ClientSessionService } from '@brighthub/uisession';
import type {
  MemberMetadataHeader,
  MemberMetadataHeaderResponse,
} from './member-metadata-header.model';

interface CacheEntry {
  payload: MemberMetadataHeader;
  fetchedAt: number;
}

/**
 * Fetches and caches the member metadata header (MW-10562) from `brightnav-api`
 * `GET /v1/prototype/client/metadata`, keyed off the active session `guid` from
 * {@link ClientSessionService}. The header component binds {@link header}, {@link loading}, and
 * {@link error} directly.
 *
 * Cache behavior (EDD 4a): per-guid TTL of {@link MemberMetadataHeaderService.CACHE_TTL_MS};
 * a valid cache entry renders immediately with no refetch; an expired entry renders immediately
 * with a background refetch; in-flight requests are **not** aborted on guid switch so returning
 * to a recently viewed client is instant.
 *
 * @remarks PII policy: never log SSN, address, phone, or DOB — non-HTTP payload errors are
 * reported to Sentry with the `guid` only. HTTP failures are already captured by {@link ApiService}.
 * Fetch failures surface via BrightNav toast ({@link MessageService}); HTTP 500 toasts are left to
 * ApiService to avoid duplicates.
 */
@Injectable({
  providedIn: 'root',
})
export class MemberMetadataHeaderService {
  private readonly _api = inject(ApiService);
  private readonly _logs = inject(StructuredLogger);
  private readonly _failedRefreshes = new Map<
    string,
    { interactionId: string; attempts: number; started: number }
  >();
  private readonly _clientSession = inject(ClientSessionService);
  private readonly _sentry = inject(SentryService);
  private readonly _messageService = inject(MessageService);

  /** Per-guid cache TTL — 5 minutes (EDD range 5–10 min; header data changes infrequently). */
  static readonly CACHE_TTL_MS = 5 * 60 * 1000;

  private readonly _cache = new Map<string, CacheEntry>();
  /** Guids with a fetch currently in flight (dedupes repeat loads; never aborted). */
  private readonly _inFlight = new Set<string>();
  /** Per-guid monotonic generation so only the latest in-flight response clears loading. */
  private readonly _fetchGeneration = new Map<string, number>();

  private readonly _requestedGuid = signal<string | null>(null);
  private readonly _header = signal<MemberMetadataHeader | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<unknown | null>(null);

  /** Active session guid (trimmed; null when no client is selected). */
  readonly activeGuid = computed(
    () => this._clientSession.selectedSubjectData()?.guid?.trim() || null,
  );

  /** Header payload for the active guid; null when nothing displayable yet. */
  readonly header = this._header.asReadonly();
  /** Guid that owns the currently exposed header payload. */
  readonly headerGuid = computed(() => this._header()?.guid?.trim() || null);
  /** Numeric client identifier resolved with the active metadata header. */
  readonly clientId = computed(() => this._header()?.clientId ?? null);
  /** True while the active guid has no displayable cache and a fetch is in flight, or during a forced refresh. */
  readonly loading = this._loading.asReadonly();
  /** Last fetch failure for the active guid; cleared on the next success. */
  readonly error = this._error.asReadonly();

  constructor() {
    effect(() => {
      const guid = this.activeGuid();
      if (guid) {
        this.load(guid);
      } else {
        this._reset();
      }
    });
  }

  /**
   * Loads the header for `guid`, making it the active guid for the exposed signals.
   * Valid cache renders with no network; expired cache renders then refetches in the background;
   * `force: true` (refresh icon) always refetches and reports {@link loading} while doing so.
   * @param guid Client guid from the session.
   * @param options `force` bypasses the cache TTL.
   * @returns void
   */
  load(guid: string, options?: { force?: boolean }): void {
    const trimmed = guid?.trim();
    if (!trimmed) {
      return;
    }
    const force = options?.force === true;
    this._requestedGuid.set(trimmed);

    const cached = this._cache.get(trimmed) ?? null;
    if (cached) {
      this._header.set(cached.payload);
      if (!force) {
        this._error.set(null);
      }
    } else {
      this._header.set(null);
    }

    const fresh =
      cached !== null &&
      Date.now() - cached.fetchedAt < MemberMetadataHeaderService.CACHE_TTL_MS;
    if (fresh && !force) {
      this._loading.set(false);
      return;
    }

    // Loading (skeleton / refresh spinner) only when there is nothing displayable or on force;
    // an expired cache entry stays visible while the background refetch runs.
    this._loading.set(force || cached === null);

    if (this._inFlight.has(trimmed) && !force) {
      return;
    }
    this._fetch(trimmed, force);
  }

  /** Drops the cache entry for `guid` so the next {@link load} refetches (future cache-bust hook). */
  invalidate(guid: string): void {
    const trimmed = guid?.trim();
    if (trimmed) {
      this._cache.delete(trimmed);
    }
  }

  /** Clears the exposed signals when no client is selected (header hidden). */
  private _reset(): void {
    this._requestedGuid.set(null);
    this._header.set(null);
    this._loading.set(false);
    this._error.set(null);
  }

  /**
   * Issues the GET and resolves it into cache + signals. Responses for guids that are no longer
   * active still populate the cache (no abort on guid switch — EDD 4a). Overlapping force refreshes
   * bump a generation so only the latest response clears {@link loading}.
   */
  private _fetch(guid: string, explicit = false): void {
    const previous = this._failedRefreshes.get(guid);
    const retry = previous && Date.now() - previous.started < 10 * 60_000;
    const operation = retry
      ? { ...previous, attempts: previous.attempts + 1 }
      : {
          interactionId: logCorrelationId(),
          attempts: 1,
          started: Date.now(),
        };
    const attrs = {
      feature: 'client_metadata',
      stage: 'refresh',
      trigger: explicit ? 'user' : 'background',
      interaction_id: operation.interactionId,
      attempt_count: operation.attempts,
    };
    const failed = (error: unknown) => {
      this._failedRefreshes.set(guid, operation);
      const oldest = this._failedRefreshes.keys().next().value;
      if (this._failedRefreshes.size > 128 && oldest !== undefined)
        this._failedRefreshes.delete(oldest);
      this._logs.emit('feature.operation.failed', {
        ...attrs,
        outcome: 'failed',
        ...logFailureReason(error),
      });
    };
    const generation = (this._fetchGeneration.get(guid) ?? 0) + 1;
    this._fetchGeneration.set(guid, generation);
    this._inFlight.add(guid);
    const path = `/v1/prototype/client/metadata?guid=${encodeURIComponent(guid)}`;
    this._api
      .get<MemberMetadataHeaderResponse>('brightnav-api', path)
      .subscribe({
        next: (res: MemberMetadataHeaderResponse & GenericResponseResult) => {
          if (this._fetchGeneration.get(guid) !== generation) {
            return;
          }
          this._inFlight.delete(guid);
          if (
            res?.result?.code !== GenericResponseCode.SERVICE_OK ||
            !res.payload
          ) {
            failed(res?.result);
            this._resolveError(
              guid,
              res?.result ?? new Error('Empty metadata header response'),
              {
                reportToSentry: true,
              },
            );
            return;
          }
          if (retry)
            this._logs.emit('feature.operation.recovered', {
              ...attrs,
              outcome: 'recovered',
            });
          this._failedRefreshes.delete(guid);
          this._cache.set(guid, {
            payload: res.payload,
            fetchedAt: Date.now(),
          });
          if (this._requestedGuid() === guid) {
            this._header.set(res.payload);
            this._loading.set(false);
            this._error.set(null);
          }
        },
        error: (err: unknown) => {
          if (this._fetchGeneration.get(guid) !== generation) {
            return;
          }
          this._inFlight.delete(guid);
          // HTTP failures are already reported by ApiService — avoid duplicate Sentry events.
          failed(err);
          this._resolveError(guid, err, { reportToSentry: false });
        },
      });
  }

  /**
   * Signals update only for the active guid. Sentry is reserved for non-HTTP payload failures;
   * HTTP transport errors are handled by ApiService. Toast uses {@link MessageService} except for
   * HTTP 500, which ApiService already toasts.
   */
  private _resolveError(
    guid: string,
    err: unknown,
    options: { reportToSentry: boolean },
  ): void {
    if (options.reportToSentry) {
      this._sentry.captureErrorWithExtras(
        err instanceof Error ? err : new Error('Metadata header fetch failed'),
        { client_guid: guid, source: 'MemberMetadataHeaderService' },
      );
    }
    if (this._requestedGuid() === guid) {
      const hasCachedHeader = this._header() !== null;
      this._error.set(err);
      this._loading.set(false);
      this._toastFetchError(err, hasCachedHeader);
    }
  }

  /**
   * Shows a BrightNav error toast unless ApiService already handled HTTP 500.
   * @param err Failure from HTTP or non-OK envelope.
   * @param hasCachedHeader When true, copy reflects a failed refresh of still-visible data.
   */
  private _toastFetchError(err: unknown, hasCachedHeader: boolean): void {
    if (this._isHttpStatus(err, 500)) {
      return;
    }
    const apiMessage = this._extractErrorMessage(err);
    const detail = hasCachedHeader
      ? 'Unable to refresh client details. Showing last loaded data.'
      : apiMessage || 'Unable to load client details.';
    this._messageService.add({
      severity: 'error',
      summary: 'Client details',
      detail,
      life: 8000,
    });
  }

  /** True only for HTTP transport errors — envelope `result.status` is not ApiService's 500 toast path. */
  private _isHttpStatus(err: unknown, status: number): boolean {
    return err instanceof HttpErrorResponse && err.status === status;
  }

  private _extractErrorMessage(err: unknown): string | null {
    if (!err || typeof err !== 'object') {
      return null;
    }
    const http = err as HttpErrorResponse & {
      error?: { result?: { message?: string }; message?: string };
      message?: string;
      result?: { message?: string };
    };
    const candidates = [
      http.error?.result?.message,
      http.error?.message,
      http.result?.message,
      err instanceof Error ? err.message : null,
      typeof http.message === 'string' ? http.message : null,
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }
    return null;
  }
}
